import { inject } from '@angular/core';
import { CanActivateFn, RedirectCommand, Router } from '@angular/router';
import { AuthService } from '../services/auth/auth.service';
import { AccessDeniedNoticeService } from '../services/common/access-denied-notice.service';

/**
 * Keeps a SuperAdmin off the conversion pages (dashboard, XBRL files, filing).
 * The backend answers every conversion call from a SuperAdmin with 403
 * `SUPERADMIN_MODULE_FORBIDDEN`, so letting the page mount would only fire
 * requests that are certain to fail.
 *
 * Redirects (never a bare `false`, which leaves a white page on a cold load)
 * to the role's home page. A dev session is left to `isAuthenticatedGuard`,
 * which already sends it to the dev area.
 *
 * ## The "no permission" toast, only for a deliberate attempt
 *
 * When the SuperAdmin asked for exactly this page (typed or bookmarked URL, a
 * refresh, a pasted filing link), the redirect also stages
 * "You don't have permission to access this page. MBRS-PERM" on
 * `AccessDeniedNoticeService`, the same line `isAdminGuard` raises. A redirect
 * that merely passes THROUGH the Dashboard (the static `'' -> mbrs/dashboard`
 * redirects, where the URL asked for was `/` or `/mbrs`) stays silent, or every
 * SuperAdmin login would greet them with a refusal they never provoked. Every
 * in-app landing hop goes to `AuthService.homeUrl()` directly instead.
 *
 * ## Fails OPEN for an unknown role (no redirect loop)
 *
 * A signed-in session whose `role` is `null` (the backend omitted it or sent a
 * value this build does not know) has `canUseConversions() === false` and a
 * `homeUrl()` of the Dashboard - the very route this guard protects. Redirecting
 * there would send the router to itself forever, re-running the parent
 * `isAuthenticatedGuard` (an `is-authenticated` request) on every lap. So only
 * a KNOWN role that cannot use conversions (today: SuperAdmin) is redirected.
 * An unknown role is admitted: this is presentation, not access control, and
 * the backend refuses whatever the role may not do. (The sidebar still shows no
 * links for an unknown role - `roleCanUseConversions(null)` stays false.)
 *
 * As a second safety, a redirect whose target is the URL already being
 * activated is never returned.
 */
export const canUseConversionsGuard: CanActivateFn = (_route, state) => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const accessDeniedNotice = inject(AccessDeniedNoticeService);

  if (auth.isDev() === true || auth.canUseConversions() || auth.role() === null) {
    return true;
  }

  const target = router.parseUrl(auth.homeUrl());
  if (router.serializeUrl(target) === router.serializeUrl(router.parseUrl(state.url))) {
    return true;
  }

  // `initialUrl` is what the user asked for, before any `redirectTo` rewrote it.
  const requestedUrl = router.getCurrentNavigation()?.initialUrl;
  if (requestedUrl && router.serializeUrl(requestedUrl) === router.serializeUrl(router.parseUrl(state.url))) {
    accessDeniedNotice.stageNoPermissionToAccessPage();
  }
  return new RedirectCommand(target);
};
